import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * PermissionManager.tsx
 * HR-only panel inside Settings → System tab.
 * Lets HR grant/revoke resource permissions for Manager and Employee roles,
 * and control which sidebar pages Employee/HR/Manager can each see.
 * HR-locked permissions (architecturally required) are read-only.
 *
 * Usage: drop inside your existing Configuration.tsx System TabPanel.
 */
import { Fragment, useState, useCallback, useEffect } from 'react';
import { Box, Typography, Paper, Chip, Switch, Checkbox, Button, Alert, Snackbar, Tooltip, CircularProgress, Divider, Tab, Tabs, Table, TableHead, TableBody, TableRow, TableCell, } from '@mui/material';
import { LockOutlined as LockIcon, CheckCircleOutline as SavedIcon, InfoOutlined as InfoIcon, } from '@mui/icons-material';
import axios from 'axios';
import { PAGES, VISIBILITY_ROLES } from '../../config/pageVisibility';
const API_URL = process.env.REACT_APP_API_URL || '/api';
// ─── Resource catalogue ───────────────────────────────────────────────────────
const RESOURCES = [
    // Training group
    {
        key: 'training',
        label: 'Training plans',
        description: 'View and manage training programs',
        group: 'Training',
        availableActions: ['create', 'read', 'update', 'delete'],
    },
    {
        key: 'trainingSchedule',
        label: 'Training schedule',
        description: 'View or edit scheduled training sessions',
        group: 'Training',
        availableActions: ['create', 'read', 'update', 'delete'],
    },
    {
        key: 'trainingMaterials',
        label: 'Training materials',
        description: 'Upload or read training content and PDFs',
        group: 'Training',
        availableActions: ['create', 'read', 'update', 'delete'],
    },
    {
        key: 'trainingFeedback',
        label: 'Training feedback',
        description: 'Submit or view post-training feedback',
        group: 'Training',
        availableActions: ['create', 'read'],
    },
    {
        key: 'trainingSuggestions',
        label: 'Training suggestions',
        description: 'Propose or manage training topics',
        group: 'Training',
        availableActions: ['create', 'read', 'update', 'delete'],
    },
    // Capabilities group
    {
        key: 'capabilityEvaluation',
        label: 'Capability evaluation',
        description: 'Evaluate team member capabilities',
        group: 'Capabilities',
        availableActions: ['create', 'read', 'update', 'delete'],
    },
    {
        key: 'capabilityAssessment',
        label: 'Capability assessment',
        description: 'Run capability assessment forms',
        group: 'Capabilities',
        availableActions: ['create', 'read', 'update', 'delete'],
    },
    {
        key: 'capabilityRoleMap',
        label: 'Role capability map',
        description: 'View role-to-capability mapping',
        group: 'Capabilities',
        availableActions: ['read'],
    },
    // Reporting group
    {
        key: 'skillGap',
        label: 'Skill gap report',
        description: 'View skill gap analysis (backend filters by team)',
        group: 'Reporting',
        availableActions: ['read'],
    },
    {
        key: 'employeeScores',
        label: 'Employee scores',
        description: 'View employee performance scores',
        group: 'Reporting',
        availableActions: ['read'],
    },
];
const GROUPS = ['Training', 'Capabilities', 'Reporting'];
// ─── Locked permissions (cannot be changed by HR) ─────────────────────────────
// These mirror the minimum permissions each role MUST have for the system to work.
const HR_LOCKED = {
    Manager: {
        capabilityEvaluation: ['create', 'read', 'update', 'delete'],
        capabilityRoleMap: ['read'],
        skillGap: ['read'],
        employeeScores: ['read'],
    },
    Employee: {
        trainingSchedule: ['read'],
        trainingMaterials: ['read'],
        trainingFeedback: ['create', 'read'],
        capabilityRoleMap: ['read'],
        skillGap: ['read'],
    },
};
// ─── Default state (mirrors your current RBAC config) ────────────────────────
const DEFAULT_PERMISSIONS = {
    Manager: {
        training: ['read'],
        trainingSchedule: ['read'],
        trainingMaterials: [],
        trainingFeedback: [],
        trainingSuggestions: ['read'],
        capabilityEvaluation: ['create', 'read', 'update', 'delete'],
        capabilityAssessment: [],
        capabilityRoleMap: ['read'],
        skillGap: ['read'],
        employeeScores: ['read'],
    },
    Employee: {
        training: [],
        trainingSchedule: ['read'],
        trainingMaterials: ['read'],
        trainingFeedback: ['create', 'read'],
        trainingSuggestions: [],
        capabilityEvaluation: [],
        capabilityAssessment: [],
        capabilityRoleMap: ['read'],
        skillGap: ['read'],
        employeeScores: [],
    },
};
function defaultPageVisibility() {
    const state = {};
    for (const role of VISIBILITY_ROLES) {
        state[role] = {};
        for (const page of PAGES) {
            state[role][page.key] = true;
            for (const sub of page.subPages ?? []) {
                state[role][sub.key] = true;
            }
        }
    }
    return state;
}
// ─── Helpers ──────────────────────────────────────────────────────────────────
function isLocked(role, resource, action) {
    return !!(HR_LOCKED[role]?.[resource]?.includes(action));
}
function hasResourceLocks(role, resource) {
    return !!(HR_LOCKED[role]?.[resource]?.length);
}
const ACTION_COLOR = {
    create: '#1D9E75',
    read: '#185FA5',
    update: '#BA7517',
    delete: '#A32D2D',
};
function ActionToggle({ action, checked, locked, onChange }) {
    const color = ACTION_COLOR[action];
    const toggle = (_jsxs(Box, { sx: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 64,
        }, children: [_jsx(Switch, { size: "small", checked: checked, onChange: onChange, disabled: locked, sx: {
                    '& .MuiSwitch-switchBase.Mui-checked': { color },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: color,
                    },
                    opacity: locked ? 0.45 : 1,
                } }), _jsx(Typography, { variant: "caption", sx: {
                    fontSize: '10px',
                    fontWeight: 500,
                    color: checked && !locked ? color : 'text.disabled',
                    textTransform: 'capitalize',
                    letterSpacing: '0.03em',
                }, children: action })] }));
    if (locked) {
        return (_jsx(Tooltip, { title: "Locked by system policy", placement: "top", arrow: true, children: _jsx(Box, { sx: { cursor: 'not-allowed' }, children: toggle }) }));
    }
    return toggle;
}
function ResourceRow({ resource, role, permissions, onToggle }) {
    const hasLocks = hasResourceLocks(role, resource.key);
    return (_jsxs(Box, { sx: {
            display: 'flex',
            alignItems: 'center',
            px: 2.5,
            py: 1.5,
            gap: 2,
            borderBottom: '0.5px solid',
            borderColor: 'divider',
            '&:last-child': { borderBottom: 'none' },
            '&:hover': { bgcolor: 'action.hover' },
            transition: 'background-color 0.15s',
        }, children: [_jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }, children: [_jsx(Typography, { variant: "body2", fontWeight: 500, sx: { fontSize: '13px' }, children: resource.label }), hasLocks && (_jsx(Chip, { icon: _jsx(LockIcon, { sx: { fontSize: '10px !important' } }), label: "System locked", size: "small", sx: {
                                    height: 18,
                                    fontSize: '10px',
                                    bgcolor: 'action.selected',
                                    color: 'text.secondary',
                                    '& .MuiChip-icon': { color: 'text.disabled' },
                                } }))] }), _jsx(Typography, { variant: "caption", color: "text.secondary", sx: { fontSize: '11px' }, children: resource.description })] }), _jsxs(Box, { sx: { display: 'flex', gap: 0.5, flexShrink: 0 }, children: [resource.availableActions.map((action) => {
                        const locked = isLocked(role, resource.key, action);
                        const checked = permissions.includes(action);
                        return (_jsx(ActionToggle, { action: action, checked: checked, locked: locked, onChange: () => !locked && onToggle(action) }, action));
                    }), ['create', 'read', 'update', 'delete']
                        .filter((a) => !resource.availableActions.includes(a))
                        .map((a) => (_jsx(Box, { sx: { minWidth: 64 } }, a)))] })] }));
}
// ─── Main Component ───────────────────────────────────────────────────────────
export default function PermissionManager() {
    const [activeTab, setActiveTab] = useState(0);
    const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
    const [pageVisibility, setPageVisibility] = useState(defaultPageVisibility());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const activeRole = activeTab === 0 ? 'Manager' : 'Employee';
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([
            axios.get(`${API_URL}/rbac/permissions`),
            axios.get(`${API_URL}/rbac/page-visibility`),
        ])
            .then(([permsRes, visRes]) => {
            if (cancelled)
                return;
            const loadedPerms = permsRes.data?.data;
            if (loadedPerms?.Manager && loadedPerms?.Employee) {
                setPermissions(loadedPerms);
            }
            const loadedVis = visRes.data?.data;
            if (loadedVis) {
                setPageVisibility((prev) => ({ ...prev, ...loadedVis }));
            }
        })
            .catch(() => {
            // Keep defaults if either fetch fails — panel stays usable.
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    const handleToggle = useCallback((resource, action) => {
        setPermissions((prev) => {
            const current = prev[activeRole][resource] ?? [];
            const next = current.includes(action)
                ? current.filter((a) => a !== action)
                : [...current, action];
            return {
                ...prev,
                [activeRole]: { ...prev[activeRole], [resource]: next },
            };
        });
    }, [activeRole]);
    const handleToggleVisibility = useCallback((role, pageKey) => {
        setPageVisibility((prev) => ({
            ...prev,
            [role]: { ...prev[role], [pageKey]: !prev[role][pageKey] },
        }));
    }, []);
    const handleSave = async () => {
        setSaving(true);
        try {
            await Promise.all([
                axios.put(`${API_URL}/rbac/permissions`, { permissions }),
                axios.put(`${API_URL}/rbac/page-visibility`, { pageVisibility }),
            ]);
            setSnackbar({ open: true, message: 'Permissions saved successfully', severity: 'success' });
        }
        catch (err) {
            setSnackbar({ open: true, message: 'Failed to save — please try again', severity: 'error' });
        }
        finally {
            setSaving(false);
        }
    };
    const handleReset = () => {
        setPermissions(DEFAULT_PERMISSIONS);
        setPageVisibility(defaultPageVisibility());
    };
    if (loading) {
        return (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', p: 4 }, children: _jsx(CircularProgress, { size: 28 }) }));
    }
    return (_jsxs(Box, { children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5 }, children: [_jsxs(Box, { children: [_jsx(Typography, { variant: "h6", fontWeight: 600, gutterBottom: true, children: "Permission Management" }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Grant or restrict resource access, and control which sidebar pages each role can see." })] }), _jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [_jsx(Button, { variant: "outlined", size: "small", onClick: handleReset, sx: { textTransform: 'none' }, children: "Reset to defaults" }), _jsx(Button, { variant: "contained", size: "small", onClick: handleSave, disabled: saving, startIcon: saving ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : _jsx(SavedIcon, { sx: { fontSize: 16 } }), sx: { textTransform: 'none', minWidth: 130 }, children: saving ? 'Saving…' : 'Save changes' })] })] }), _jsxs(Alert, { icon: _jsx(InfoIcon, { fontSize: "small" }), severity: "info", sx: { mb: 2.5, fontSize: '12px' }, children: ["Permissions marked ", _jsx("strong", { children: "System locked" }), " are required for core system functionality and cannot be changed. All other permissions can be freely adjusted per role."] }), _jsxs(Paper, { variant: "outlined", sx: { mb: 3 }, children: [_jsxs(Tabs, { value: activeTab, onChange: (_, v) => setActiveTab(v), sx: { borderBottom: 1, borderColor: 'divider', px: 1 }, children: [_jsx(Tab, { label: "Manager", sx: { textTransform: 'none', fontSize: '13px' } }), _jsx(Tab, { label: "Employee", sx: { textTransform: 'none', fontSize: '13px' } })] }), _jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            px: 2.5,
                            py: 1,
                            bgcolor: 'action.hover',
                            borderBottom: '0.5px solid',
                            borderColor: 'divider',
                        }, children: [_jsx(Typography, { variant: "caption", color: "text.disabled", sx: { flex: 1, fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }, children: "Resource" }), ['create', 'read', 'update', 'delete'].map((a) => (_jsx(Typography, { variant: "caption", color: "text.disabled", sx: { minWidth: 64, textAlign: 'center', fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }, children: a }, a)))] }), GROUPS.map((group) => {
                        const items = RESOURCES.filter((r) => r.group === group);
                        return (_jsxs(Box, { children: [_jsx(Box, { sx: { px: 2.5, py: 0.75, bgcolor: 'background.default' }, children: _jsx(Typography, { variant: "caption", sx: {
                                            fontSize: '10px',
                                            fontWeight: 700,
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            color: 'text.disabled',
                                        }, children: group }) }), items.map((resource) => (_jsx(ResourceRow, { resource: resource, role: activeRole, permissions: permissions[activeRole][resource.key] ?? [], onToggle: (action) => handleToggle(resource.key, action) }, resource.key))), _jsx(Divider, {})] }, group));
                    })] }), _jsx(Typography, { variant: "h6", fontWeight: 600, gutterBottom: true, children: "Page Visibility" }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { mb: 1.5 }, children: "Controls which sidebar pages Employee, HR, and Manager can each see. Admin always sees everything. Profile and Configuration always stay visible for every role." }), _jsx(Paper, { variant: "outlined", children: _jsxs(Table, { size: "small", children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'text.disabled' }, children: "Page" }), VISIBILITY_ROLES.map((role) => (_jsx(TableCell, { align: "center", sx: { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'text.disabled' }, children: role }, role)))] }) }), _jsx(TableBody, { children: PAGES.map((page) => (_jsxs(Fragment, { children: [_jsxs(TableRow, { hover: true, children: [_jsx(TableCell, { sx: { fontSize: '13px' }, children: page.label }), VISIBILITY_ROLES.map((role) => (_jsx(TableCell, { align: "center", children: _jsx(Checkbox, { size: "small", checked: pageVisibility[role]?.[page.key] ?? true, onChange: () => handleToggleVisibility(role, page.key) }) }, role)))] }), page.subPages?.map((sub) => (_jsxs(TableRow, { hover: true, sx: { bgcolor: 'action.hover' }, children: [_jsx(TableCell, { sx: { fontSize: '12px', color: 'text.secondary', pl: 4 }, children: sub.label }), VISIBILITY_ROLES.map((role) => {
                                                const parentVisible = pageVisibility[role]?.[page.key] ?? true;
                                                return (_jsx(TableCell, { align: "center", children: _jsx(Checkbox, { size: "small", checked: parentVisible && (pageVisibility[role]?.[sub.key] ?? true), disabled: !parentVisible, onChange: () => handleToggleVisibility(role, sub.key) }) }, role));
                                            })] }, sub.key)))] }, page.key))) })] }) }), _jsx(Snackbar, { open: snackbar.open, autoHideDuration: 3500, onClose: () => setSnackbar((s) => ({ ...s, open: false })), anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, children: _jsx(Alert, { severity: snackbar.severity, onClose: () => setSnackbar((s) => ({ ...s, open: false })), children: snackbar.message }) })] }));
}
